import React, { useEffect, useState } from "react";
import { MapPin, Heart, Leaf, ArrowRight, Phone, Mail, Globe, BookOpen, Coffee, Home, Sparkles, Target, Baby, Flame, Users, Brain, Flower2, Wand2, Smile, Droplets, User, Star, Book, Gem, Sun, Moon, } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
import { BOOKING_URL } from '@/constants/urls';
import treatmentImage from '@/assets/treatment.jpg';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import OfferPopup from '@/components/OfferPopup';

const CosmeticAcupuncturePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showOfferPopup, setShowOfferPopup] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Cosmetic Acupuncture & Beauty Rituals Sunshine Coast | Natural Beauty";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Glow from within - naturally, intentionally, beautifully. Cosmetic acupuncture and beauty rituals with Chinese medicine in Currimundi.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Cosmetic Acupuncture & Beauty Rituals Sunshine Coast | Natural Beauty");
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Glow from within - naturally, intentionally, beautifully. Cosmetic acupuncture and beauty rituals with Chinese medicine in Currimundi.");
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/cosmetic-acupuncture");
    }
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/treatment.jpg");
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Cosmetic Acupuncture & Beauty Rituals Sunshine Coast | Natural Beauty");
    }
    const schema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Cosmetic Acupuncture & Beauty Rituals",
      "image": "https://womenandtheway.com.au/treatment.jpg",
      "description": "Glow from within - naturally, intentionally, beautifully. Cosmetic acupuncture and beauty rituals with Chinese medicine in Currimundi.",
      "url": "https://womenandtheway.com.au/cosmetic-acupuncture",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Cosmetic Acupuncture, Beauty Rituals, Anti-Aging, Chinese Medicine",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  Cosmetic Acupuncture & Beauty Rituals
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Glow from within - naturally, intentionally, beautifully.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Cosmetic acupuncture is a gentle, natural approach to skin health and anti-aging that works from the inside out. At Women and the Way, we combine cosmetic acupuncture techniques with whole-body wellness to help you look and feel your best.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Whether you're addressing fine lines, acne, skin texture, or simply want to maintain a healthy glow, our treatments help restore your skin's natural vitality. Our approach combines the wisdom of Traditional Chinese Medicine with modern understanding of skin health. Many of our clients also benefit from our <a href="/skin-health" className="text-sage-600 hover:text-sage-800 underline transition-colors">skin health services</a> and <a href="/stress-burnout" className="text-sage-600 hover:text-sage-800 underline transition-colors">stress relief treatments</a>.
                </p>
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={treatmentImage} 
                  alt="Cosmetic acupuncture and beauty rituals for natural beauty and radiance" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>
        {/* What Is Cosmetic Acupuncture? */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Wand2 className="w-8 h-8 text-sage-600" />
                What Is Cosmetic Acupuncture?
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                Cosmetic acupuncture is a gentle and effective treatment that stimulates collagen production, improves circulation, and tones facial muscles - all while treating the root causes of aging and skin concerns from a holistic Chinese medicine perspective.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                It's not just skin-deep: we work with internal imbalances, stress patterns, digestion, hormones, and blood flow to restore glow, vitality, and confidence.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                Each treatment includes full-body acupuncture to address your constitution, followed by delicate facial needling, gua sha, massage, and curated skin rituals.
              </p>
            </div>
          </div>
        </section>
        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Cosmetic Acupuncture & Ritual Care in Clinic
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Smile className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Signature Cosmetic Acupuncture Sessions</h3>
                  </div>
                  <p className="text-sage-700 mb-4">
                    A deeply restorative treatment that leaves you glowing - inside and out.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">Cosmetic acupuncture to tone skin, soften fine lines, reduce puffiness, and stimulate collagen</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Body acupuncture to address the root causes: digestion, stress, hormonal changes, circulation</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💆‍♀️</span>
                      <p className="text-sage-700">Gua sha and facial massage to sculpt, lift, and relax the face</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🧖‍♀️</span>
                      <p className="text-sage-700">Custom masks, botanical serums, or moxa as needed to nourish your skin and spirit</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🎓</span>
                      <p className="text-sage-700">Practitioner trained in advanced cosmetic acupuncture techniques, including the Wakefield Method and Michelle Gellis protocols</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
              Ready to Glow?
            </h2>
            <p className="text-xl text-sage-600 mb-8 max-w-2xl mx-auto">
              Book your cosmetic acupuncture session and discover the natural path to radiant skin and inner confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-medium"
              >
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book Your Session
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-4 text-lg font-medium"
              >
                <a href="tel:0415537784">Call 0415 537 784</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
      <ReturnToTop />
      <ScrollDownArrow />
      <OfferPopup 
        isOpen={showOfferPopup}
        onClose={() => setShowOfferPopup(false)}
        offerType="intro-skin"
        showOnPages={['/cosmetic-acupuncture']}
        delay={5000}
      />
    </>
  );
};

export default CosmeticAcupuncturePage; 